import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Box,
  Chip,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { isString } from 'lodash';
import React, { FC, useState } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';

import {
  getHotkeyLabelForNav,
  getHotkeyLabelForPath,
} from '../../../../features/HotkeySettings/hotkeys';
import { useHotkeySettings } from '../../../../features/HotkeySettings/context/HotkeySettings';
import useUser from '../../../../hooks/useUser';
import NavigationLink from '../../../../interfaces/NavigationLink';
import { classnames } from '../../../../utils';
import SidebarList from '../SidebarList/SidebarList';
import styles from './SidebarListItem.module.scss';

const HotkeyBadge: FC<{ label: string }> = ({ label }) => (
  <Typography
    variant='caption'
    sx={{
      ml: 1,
      px: 0.75,
      py: 0.125,
      borderRadius: 1,
      color: 'text.secondary',
      border: '1px solid',
      borderColor: 'divider',
      fontSize: '0.625rem',
      lineHeight: 1.4,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Typography>
);

interface SidebarListItemProps extends NavigationLink {
  isParentElement?: boolean;
  onCloseSidebar: () => void;
  onActive: (content: NavigationLink) => void;
}

const SidebarListItem: FC<SidebarListItemProps> = ({
  label,
  content,
  icon: Icon,
  isParentElement = false,
  externalLink,
  isNew,
  disabled,
  locked,
  notSubscribed,
  comingSoon,
  onCloseSidebar,
  onActive,
  requiredPermissions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const user = useUser();
  const { pathname } = useLocation();
  const { settings: hotkeySettings } = useHotkeySettings();
  const showHotkeyBadges =
    hotkeySettings.enabled && hotkeySettings.showBadges;
  const hotkeyLabel =
    showHotkeyBadges && isString(content)
      ? getHotkeyLabelForPath(content, hotkeySettings.hotkeys)
      : undefined;

  const handleCollapseToggle = (): void => {
    setIsOpen((isOpen) => !isOpen);
  };

  const listItemIcon = isParentElement && Icon && (
    <ListItemIcon className={styles.listItemIcon}>
      <Icon color='primary' />
    </ListItemIcon>
  );

  // Modules that are not yet released are shown but non-clickable, with a
  // "Coming Soon" chip. Takes precedence over the locked/permission state.
  if (comingSoon)
    return (
    <ListItemButton
        disableRipple
        disabled
        id={styles.sidebarListItem}
        sx={{ pointerEvents: 'none', opacity: 0.7 }}
      >
        {listItemIcon}
        <ListItemText primary={label} sx={{ color: 'secondary.dark' }} />
        <Chip
          label='Coming Soon'
          size='small'
          color='primary'
          variant='outlined'
          sx={{ height: 20, fontSize: '0.625rem' , marginLeft:1}}
        />
      </ListItemButton>
    );

  // Modules the business has not purchased a subscription for: greyed out,
  // non-clickable, marked with a premium/upgrade indicator to communicate
  // that access requires a subscription change (vs a role permission).
  if (notSubscribed)
    return (
      <Tooltip title='Not included in your subscription' placement='right' arrow>
        <span style={{ display: 'block', cursor: 'not-allowed' }}>
          <ListItemButton
            disableRipple
            disabled
            id={styles.sidebarListItem}
            sx={{ pointerEvents: 'none', opacity: 0.6, '& a': { pointerEvents: 'auto' } }}
          >
            {listItemIcon}
            <ListItemText primary={label} sx={{ color: 'secondary.dark' }} />
            <Box
              component='a'
              href='mailto:care@casaretail.ai'
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                border: '1px solid',
                borderColor: 'text.disabled',
                borderRadius: 1,
                color: 'text.secondary',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': { borderColor: 'text.secondary' },
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: '0.7rem' }} />
              <Typography
                variant='caption'
                sx={{ fontSize: '0.625rem', fontWeight: 600, lineHeight: 1.4 }}
              >
                UPGRADE
              </Typography>
            </Box>
          </ListItemButton>
        </span>
      </Tooltip>
    );

  // Modules the user's role does not grant access to: greyed out,
  // non-clickable, marked with a lock indicator. The business is subscribed,
  // so the remediation is to request access from an admin.
  if (locked)
    return (
      <Tooltip
        title='Your role does not have access to this module'
        placement='right'
        arrow
      >
        <span style={{ display: 'block', cursor: 'not-allowed' }}>
          <ListItemButton
            disableRipple
            disabled
            id={styles.sidebarListItem}
            sx={{ pointerEvents: 'none', opacity: 0.6 }}
          >
            {listItemIcon}
            <ListItemText primary={label} sx={{ color: 'secondary.dark' }} />
            <LockOutlinedIcon fontSize='small' color='disabled' />
          </ListItemButton>
        </span>
      </Tooltip>
    );

  // TODO: Need to add DUO link for handling external link and internal link - This is temp fix
  if (isString(content) && externalLink) {
    const src = new URL(content);
    user?.selectedBusiness?.id &&
      src.searchParams.append('bu', user.selectedBusiness.id.toString());
    src.searchParams.append('ref', 'v2');

    return (
      <ListItemButton
        target='_blank'
        disableRipple
        id={styles.sidebarListItem}
        href={src.href}
        onClick={onCloseSidebar}
        disabled={disabled}
      >
        {listItemIcon}
        <ListItemText primary={label} sx={{ color: 'secondary.dark' }} />
        {isNew ? <FiberNewIcon color='primary' /> : null}
      </ListItemButton>
    );
  }

  if (typeof content === 'string' && matchPath(content, pathname))
    onActive({
      label,
      content,
      icon: Icon,
      externalLink,
      isNew,
      disabled,
      requiredPermissions,
    });

  if (isString(content))
    return (
      <ListItemButton
        disableRipple
        id={styles.sidebarListItem}
        component={NavLink}
        end
        to={content}
        disabled={disabled}
        onClick={onCloseSidebar}
        
      >
        {listItemIcon}
        <ListItemText
          className={styles.sidebarListItemText}
          primaryTypographyProps={{
            className: styles.sidebarListItemTypography,
          }}
          primary={label}
          sx={{ color: 'secondary.dark' }}
        />
        {isNew ? <FiberNewIcon color='primary' /> : null}
        {hotkeyLabel && <HotkeyBadge label={hotkeyLabel} />}
      </ListItemButton>
    );

  if (content.length === 0) return null;

  const getActiveListItem = (list: NavigationLink[]): NavigationLink | null => {
    let selectedItem: NavigationLink | null = null;
    for (let index = 0; index < list.length; index++) {
      const item = list[index];
      if (
        typeof item.content === 'string' &&
        matchPath(item.content, pathname)
      ) {
        selectedItem = item;
        break;
      } else if (Array.isArray(item.content))
        return getActiveListItem(item.content);
    }

    return selectedItem;
  };

  const activeListItem = getActiveListItem(content);

  // Surface a child's shortcut on the collapsed parent so it is discoverable
  // without opening the dropdown.
  const groupHotkeyLabel = showHotkeyBadges
    ? content.reduce<string | undefined>(
        (found, child) =>
          found ?? getHotkeyLabelForNav(child, hotkeySettings.hotkeys),
        undefined
      )
    : undefined;

  return (
    <>
      <ListItemButton
        disableRipple
        id={styles.sidebarListItem}
        onClick={handleCollapseToggle}
        disabled={disabled}
      >
        {listItemIcon}
        <ListItemText primary={label} sx={{ color: 'secondary.dark' }} />
        {isNew ? <FiberNewIcon color='primary' /> : null}
        {!isOpen && groupHotkeyLabel && (
          <HotkeyBadge label={groupHotkeyLabel} />
        )}
        <ChevronRightIcon
          fontSize='small'
          color='disabled'
          className={classnames(styles.arrowIcon, {
            [styles.active]: isOpen,
          })}
        />
      </ListItemButton>
      <Collapse in={isOpen} mountOnEnter>
        <SidebarList
          onCloseSidebar={onCloseSidebar}
          onActive={onActive}
          list={content}
        />
      </Collapse>
      {!isOpen && !!activeListItem && (
        <ListItemButton
          disableRipple
          id={styles.sidebarListItem}
          className={styles.activeListItem}
        >
          <ListItemText
            className={styles.sidebarListItemText}
            primaryTypographyProps={{
              className: styles.sidebarListItemTypography,
            }}
            primary={activeListItem.label as string}
            sx={{ color: 'secondary.dark' }}
          />
        </ListItemButton>
      )}
    </>
  );
};

export default SidebarListItem;
